import { useEffect, useRef, useState } from 'react';

import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

import { LAYER_BORDER_DEFAULT, LAYER_BORDER_HOVERED } from '../../constants/colors.ts';
import { useModelContext } from '../../context/modelContext.ts';
import { ModelLayer } from '../model-context/ModelContext.ts';
import { LayerSetting } from '../project-settings/types.ts';
import { getLayerColor, shouldIncludeMesh } from './utils.ts';

interface OBJModelProps {
  url: string;
  name: string;
  isHoveredName: string | null;
  selectedLayer?: string | null;
  position: [number, number, number];
  onHover: (name: string | null) => void;
  onClick: (name: string) => void;
  isHovered: boolean;
  onLayersFound?: (modelName: string, layers: ModelLayer[]) => void;
  visibleLayers?: string[];
  layerSettings: LayerSetting[];
  isLoaded?: boolean;
}

// Component to load and display an OBJ model
export function OBJModel({
  url,
  name,
  isHoveredName,
  selectedLayer,
  position,
  onHover,
  onClick,
  isHovered,
  onLayersFound,
  visibleLayers,
  layerSettings,
  isLoaded,
}: OBJModelProps) {
  const centerRef = useRef<THREE.Vector3 | null>(null);
  const [meshes, setMeshes] = useState<THREE.Mesh[]>([]);
  const [hoveredMeshName, setHoveredMeshName] = useState<string | null>(null);
  const modelRef = useRef<THREE.Group>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { onModelLoadingError } = useModelContext();

  // Load the OBJ model
  useEffect(() => {
    // Skip loading if the model is already loaded
    if (isLoaded && meshes.length > 0) {
      setIsLoading(false);
      return;
    }

    const loader = new OBJLoader();
    setIsLoading(true);

    loader.load(
      url,
      (obj) => {
        const foundMeshes: THREE.Mesh[] = [];
        const extractedLayers: ModelLayer[] = [];

        // Calculate the total bounding box and center
        if (centerRef.current) {
          const totalBox = new THREE.Box3().setFromObject(obj);
          const center = new THREE.Vector3();
          totalBox.getCenter(center);

          centerRef.current = center;
        }

        obj.traverse((object) => {
          // Only include actual meshes with meaningful names
          if (object instanceof THREE.Mesh && shouldIncludeMesh(object)) {
            const mesh = object.clone();

            // Get the mesh's position relative to the model center
            const meshPosition = new THREE.Vector3();
            mesh.getWorldPosition(meshPosition);

            if (centerRef.current) {
              meshPosition.sub(centerRef.current);
            }

            // Apply position adjustment but keep original scale
            mesh.position.copy(meshPosition);

            // Update geometry
            mesh.geometry.computeBoundingBox();
            mesh.geometry.computeBoundingSphere();
            mesh.geometry.computeVertexNormals();

            mesh.castShadow = true;
            mesh.receiveShadow = true;

            foundMeshes.push(mesh);

            extractedLayers.push({
              uuid: mesh.uuid,
              threeJsName: object.name,
              originalName: object.name,
              displayName: object.name, // Default to object name
              modelName: name,
              visible: true,
            });
          }
        });

        // If no meaningful meshes were found, include all meshes
        if (foundMeshes.length === 0) {
          obj.traverse((object) => {
            if (object instanceof THREE.Mesh) {
              const mesh = object.clone();
              const meshName = object.name || `Mesh_${foundMeshes.length}`;
              mesh.name = meshName;

              // Get the mesh's position relative to the model center
              const meshPosition = new THREE.Vector3();
              mesh.getWorldPosition(meshPosition);
              if (centerRef.current) {
                meshPosition.sub(centerRef.current);
              }

              // Apply position adjustment but keep original scale
              mesh.position.copy(meshPosition);

              // Update geometry
              mesh.geometry.computeBoundingBox();
              mesh.geometry.computeBoundingSphere();
              mesh.geometry.computeVertexNormals();

              mesh.castShadow = true;
              mesh.receiveShadow = true;

              foundMeshes.push(mesh);

              extractedLayers.push({
                uuid: mesh.uuid,
                threeJsName: meshName,
                originalName: meshName,
                displayName: meshName, // Default to mesh name
                modelName: name,
                visible: true,
              });
            }
          });
        }

        setMeshes(foundMeshes);
        setIsLoading(false);

        // Check if model has no meshes/layers
        if (foundMeshes.length === 0) {
          onModelLoadingError(name, 'Model contains no valid meshes or layers');
          return;
        }

        // Notify parent component about found layers
        if (onLayersFound && extractedLayers.length > 0) {
          onLayersFound(name, extractedLayers);
        }
      },
      undefined,
      (error) => {
        console.error('Error loading OBJ model:', error);
        setIsLoading(false);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred while loading the model';
        onModelLoadingError(name, errorMessage);
      },
    );
  }, [url, name, onLayersFound, isLoaded, meshes.length, onModelLoadingError]);

  // Handle hover state
  useEffect(() => {
    if (isHovered) {
      onHover(isHoveredName);
    }
  }, [isHovered, isHoveredName, onHover]);

  if (isLoading) {
    return (
      <group position={position}>
        <mesh>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial color="#333333" wireframe={true} />
        </mesh>
      </group>
    );
  }

  return (
    <group ref={modelRef} position={position}>
      {meshes.map((mesh, index) => {
        const isThisMeshHovered = isHoveredName === mesh.name || hoveredMeshName === mesh.name || isHovered === true;
        const isThisMeshSelected = selectedLayer === mesh.name;
        const isVisible = !visibleLayers || visibleLayers.includes(mesh.name);

        const layerColor = getLayerColor(mesh.name, layerSettings);
        const layerSetting = layerSettings.find((s) => s.threeJsName === mesh.name);
        const hasChlOrLmn = !!layerSetting?.canHoldLight || !!layerSetting?.illuminated;

        // Skip rendering if the layer is not visible
        if (!isVisible) return null;

        const borderColor = isThisMeshHovered ? LAYER_BORDER_HOVERED : LAYER_BORDER_DEFAULT;
        const borderOpacity = isThisMeshHovered ? 0.9 : 0.8;
        const offsets = [
          0, 0.004, -0.004, 0.008, -0.008, 0.012, -0.012, 0.016, -0.016, 0.002, -0.002, 0.006, -0.006, 0.01, -0.01,
          0.014, -0.014, 0.018, -0.018, 0.02, -0.02,
        ];

        return (
          <group key={`mesh-${index}`}>
            <mesh
              geometry={mesh.geometry}
              position={mesh.position}
              rotation={mesh.rotation}
              scale={mesh.scale}
              name={mesh.name}
              onPointerOver={(e) => {
                e.stopPropagation();
                setHoveredMeshName(mesh.name);
                onHover(mesh.name);
              }}
              onPointerOut={(e) => {
                e.stopPropagation();
                setHoveredMeshName(null);
                onHover(null);
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClick(mesh.name);
              }}
            >
              {/* Priority: 1.hover 2.CHL/LMN 3.selected 4.grey */}
              <meshStandardMaterial
                color={
                  isThisMeshHovered ? '#ff9136' : hasChlOrLmn ? layerColor : isThisMeshSelected ? '#ef4444' : layerColor
                }
                emissive={
                  isThisMeshHovered ? '#ff9136' : hasChlOrLmn ? layerColor : isThisMeshSelected ? '#ef4444' : layerColor
                }
                emissiveIntensity={isThisMeshHovered ? 0.5 : hasChlOrLmn ? 0.5 : isThisMeshSelected ? 0.4 : 0.15}
                transparent={true}
                opacity={isThisMeshHovered ? 0.8 : isThisMeshSelected ? 0.85 : 0.65}
                wireframe={false}
                side={THREE.DoubleSide}
              />
            </mesh>

            {/* Edge borders — multiple passes for thickness */}
            {offsets.map((offset, oi) => (
              <lineSegments
                key={`edge-${oi}`}
                position={[mesh.position.x + offset, mesh.position.y + offset, mesh.position.z + offset]}
                rotation={mesh.rotation}
                scale={mesh.scale}
                raycast={() => {}}
              >
                <edgesGeometry args={[mesh.geometry, 30]} />
                <lineBasicMaterial
                  color={borderColor}
                  transparent
                  opacity={oi === 0 ? borderOpacity : borderOpacity * 0.7}
                />
              </lineSegments>
            ))}
          </group>
        );
      })}

      {/* Model info */}
      {isHovered && (
        <group>
          <Text color="#FFFFFF" fontSize={0.2} anchorX="center" anchorY="middle" position={[0, 1.5, 0]}>
            {name}
          </Text>
          <Text color="#B3EC49" fontSize={0.15} anchorX="center" anchorY="middle" position={[0, 1.2, 0]}>
            {`${meshes.length} mesh layers`}
          </Text>
        </group>
      )}
    </group>
  );
}
